import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import use3DModelThumbnail, { is3DModelUrl } from '../../../hooks/use3DModelThumbnail';
import { wishlistService } from '../../../services/wishlistService';
import { useAuth } from '../../../contexts/AuthContext';
import CommuneTourBadge from './CommuneTourBadge';

const PropertyCard = ({ property, onInquiry }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // 3D model thumbnail — only runs when videoUrl is a .obj/.glb file
  const model3DUrl = is3DModelUrl(property?.videoUrl) ? property?.videoUrl : null;
  const { thumbnail: model3DThumbnail, generating: generatingThumb } = use3DModelThumbnail(model3DUrl);

  const handleVideoToggle = () => {
    if (videoRef?.current) {
      if (isVideoPlaying) {
        videoRef?.current?.pause();
      } else {
        videoRef?.current?.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef?.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatPrice = (price, marketType, rentalPeriod) => {
    const formattedPrice = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);

    if (marketType === 'rental') {
      const periodSuffix = rentalPeriod === 'monthly' ? '/mois' :
      rentalPeriod === 'yearly' ? '/an' : '';
      return `${formattedPrice}${periodSuffix}`;
    }

    return formattedPrice;
  };

  const getMarketTypeLabel = (marketType) => {
    return marketType === 'rental' ? 'À louer' :
    marketType === 'sale' ? 'À vendre' : 'Propriété';
  };

  const getVerificationBadge = (status) => {
    const badges = {
      verified: { color: 'bg-emerald-500/10 text-emerald-400', text: 'Vérifié', icon: 'CheckCircle' },
      pending: { color: 'bg-yellow-500/10 text-yellow-400', text: 'En cours', icon: 'Clock' },
      unverified: { color: 'bg-white/10 text-white/50', text: 'Non vérifié', icon: 'AlertCircle' }
    };
    return badges?.[status] || badges?.unverified;
  };

  const handleQuickInquiry = (type) => {
    const inquiryData = {
      propertyId: property?.id,
      type,
      property: property?.title
    };

    // Call the parent handler which will save to database
    onInquiry(inquiryData);
    setShowInquiryForm(false);
  };

  const badge = getVerificationBadge(property?.verificationStatus);

  // Determine what to render in the media container
  const is3DModel = is3DModelUrl(property?.videoUrl);
  const mediaThumbnail = is3DModel ?
  model3DThumbnail || property?.thumbnail :
  property?.thumbnail;

  const handleLaunch3DTour = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    navigate(`/visite-3d/${property?.id}`);
  };

  // Check wishlist status on mount
  useEffect(() => {
    if (!user?.id || !property?.id) return;
    wishlistService?.isInWishlist(user?.id, property?.id)?.then(({ data }) => {
      setIsWishlisted(!!data);
    });
  }, [user?.id, property?.id]);

  const handleWishlistToggle = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user?.id) return;
    setWishlistLoading(true);
    try {
      const { added } = await wishlistService?.toggleWishlist(user?.id, property);
      setIsWishlisted(added);
    } catch (err) {
      console.error('Wishlist toggle error:', err);
    } finally {
      setWishlistLoading(false);
    }
  };

  return (
    <div className="bg-[#111111] rounded-3xl overflow-hidden shadow-2xl hover:-translate-y-1 transition-all duration-300 group border-none outline-none flex flex-col">
      {/* Media Container */}
      <div className="relative aspect-video bg-[#0A0A0A] overflow-hidden">
        {property?.videoUrl && !is3DModel ?
        <>
            <video
            ref={videoRef}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            poster={property?.thumbnail}
            muted={isMuted}
            loop
            playsInline
            onPlay={() => setIsVideoPlaying(true)}
            onPause={() => setIsVideoPlaying(false)}>

              <source src={property?.videoUrl} type="video/mp4" />
            </video>
            
            {/* Video Controls — always visible on mobile */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-4 left-4 flex items-center gap-2">
                <button
                onClick={handleVideoToggle}
                className="w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-brand-gold hover:text-black active:scale-95 transition-all">
                  <Icon
                  name={isVideoPlaying ? "Pause" : "Play"}
                  size={18} />
                </button>
                <button
                onClick={handleMuteToggle}
                className="w-10 h-10 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-brand-gold hover:text-black active:scale-95 transition-all">
                  <Icon
                  name={isMuted ? "VolumeX" : "Volume2"}
                  size={18} />
                </button>
              </div>
            </div>
          </> :
        is3DModel ? (
        /* ── 3D Model Preview ─────────────────────────────────────────── */
        <div className="relative w-full h-full">
            {mediaThumbnail ?
          <img
            src={mediaThumbnail}
            alt={`Aperçu 3D — ${property?.title}`}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /> :

          <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center">
                <Icon name="Box" size={40} className="text-white/20" />
              </div>
          }
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-black/30 opacity-80" />

            {/* Generating spinner overlay */}
            {generatingThumb && !model3DThumbnail &&
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center space-y-3">
                <div className="w-8 h-8 border-2 border-white/20 border-t-brand-gold rounded-full animate-spin" />
                <span className="text-brand-gold text-[10px] font-bold uppercase tracking-widest">Génération 3D…</span>
              </div>
          }
            {/* View in 3D hint on hover */}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
              <div className="bg-brand-gold text-black text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full flex items-center gap-2 shadow-lg shadow-brand-gold/20">
                <Icon name="RotateCcw" size={14} />
                <span>Explorer en 3D</span>
              </div>
            </div>
          </div>) :

        <div className="relative w-full h-full">
          <Image
            src={property?.thumbnail}
            alt={property?.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-black/30 opacity-80" />
        </div>

        }

        {/* Market Type Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1.5 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg ${
          property?.market_type === 'rental' ? 'bg-blue-600' :
          property?.market_type === 'sale' ? 'bg-brand-gold text-black' : 'bg-white/20 backdrop-blur-md'}`
          }>
            {getMarketTypeLabel(property?.market_type)}
          </span>
        </div>

        {/* Verification Badge */}
        <div className="absolute top-4 right-4">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest backdrop-blur-md shadow-lg ${badge?.color}`}>
            <Icon name={badge?.icon} size={12} />
            <span className="hidden sm:inline">{badge?.text}</span>
          </div>
        </div>

        {/* Notaire Verified Badge */}
        {property?.is_notary_verified &&
        <div className="absolute top-14 right-4 sm:top-4 sm:right-32">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-emerald-500/90 text-white shadow-lg backdrop-blur-md">
            <Icon name="BadgeCheck" size={12} color="white" />
            <span>Vérifié</span>
          </div>
        </div>
        }

        {/* Quick Actions overlay */}
        <div className="absolute bottom-4 right-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowInquiryForm(true)}
              className="w-10 h-10 bg-black/60 backdrop-blur-md border-none rounded-full flex items-center justify-center hover:bg-brand-gold hover:text-black text-white active:scale-95 transition-all shadow-lg">

              <Icon name="MessageCircle" size={18} />
            </button>
            {user &&
            <button
              onClick={handleWishlistToggle}
              disabled={wishlistLoading}
              className="w-10 h-10 bg-black/60 backdrop-blur-md border-none rounded-full flex items-center justify-center hover:bg-black/80 active:scale-95 transition-all shadow-lg disabled:opacity-60"
              aria-label={isWishlisted ? 'Retirer des favoris' : 'Ajouter aux favoris'}>

                {wishlistLoading ?
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> :

              <Icon
                name="Heart"
                size={18}
                color={isWishlisted ? '#e11d48' : 'white'}
                fill={isWishlisted ? '#e11d48' : 'none'} />

              }
              </button>
            }
          </div>
        </div>

        {/* Visite 3D floating button (Mobile/Always visible fallback) */}
        <div className="absolute bottom-4 left-4 sm:hidden">
          <button
            onClick={handleLaunch3DTour}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg transition-transform hover:scale-105 active:scale-95 ${
            is3DModel ?
            'bg-brand-gold text-black' : 'bg-black/60 backdrop-blur-md text-white'}`
            }>

            <Icon name="Box" size={12} />
            <span>3D</span>
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6 flex-grow flex flex-col justify-between border-none">
        
        <div className="mb-6">
          {/* Title & Location */}
          <Link
            to={`/individual-property-page/${property?.id}`}
            className="block hover:text-brand-gold transition-colors mb-2">
            <h3 className="font-heading font-bold text-lg text-white line-clamp-2 leading-snug">
              {property?.title}
            </h3>
          </Link>
          
          <div className="flex items-center gap-1.5 text-white/50">
            <Icon name="MapPin" size={14} />
            <span className="text-sm font-light truncate">{property?.commune || property?.address}</span>
            {(property?.commune_id || property?.commune) && (
              <CommuneTourBadge
                communeName={property?.commune}
                communeId={property?.commune_id}
              />
            )}
          </div>
        </div>

        {/* Price & Metrics */}
        <div className="space-y-3 mb-6">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-heading font-bold text-brand-gold tracking-tight">
              {formatPrice(property?.price, property?.market_type, property?.rental_period)}
            </span>
            {property?.pricePerSqm &&
            <span className="text-xs font-bold text-white/30 uppercase tracking-widest">
                {formatPrice(property?.pricePerSqm, property?.market_type, property?.rental_period)}/m²
              </span>
            }
          </div>

          <div className="flex items-center gap-4">
            {property?.market_type === 'rental' && property?.rental_period &&
            <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-md">
                <Icon name="Calendar" size={12} className="text-white/40" />
                <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">
                  {property?.rental_period === 'monthly' ? 'Mensuel' : 'Annuel'}
                </span>
              </div>
            }

            {property?.estimatedROI &&
            <div className="flex items-center gap-1.5 bg-emerald-500/10 px-2.5 py-1 rounded-md">
                <Icon name="TrendingUp" size={12} className="text-emerald-400" />
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">
                  ROI: {property?.estimatedROI}%
                </span>
              </div>
            }
          </div>
        </div>

        {/* Property Details Grid */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {property?.bedrooms &&
          <div className="flex flex-col items-center justify-center bg-white/5 rounded-xl py-3 border-none">
              <Icon name="Bed" size={16} className="text-brand-gold mb-1" />
              <span className="text-sm font-bold text-white">{property?.bedrooms}</span>
            </div>
          }
          {property?.bathrooms &&
          <div className="flex flex-col items-center justify-center bg-white/5 rounded-xl py-3 border-none">
              <Icon name="Bath" size={16} className="text-brand-gold mb-1" />
              <span className="text-sm font-bold text-white">{property?.bathrooms}</span>
            </div>
          }
          {property?.area &&
          <div className="flex flex-col items-center justify-center bg-white/5 rounded-xl py-3 border-none">
              <Icon name="Square" size={16} className="text-brand-gold mb-1" />
              <span className="text-sm font-bold text-white">{property?.area}<span className="text-[10px]">m²</span></span>
            </div>
          }
        </div>

        {/* Amenities */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {property?.hasGenerator &&
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-md">
              <Icon name="Zap" size={10} />
              <span>Générateur</span>
            </div>
          }
          {property?.hasParking &&
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-white/5 text-white/60 px-2 py-1 rounded-md">
              <Icon name="Car" size={10} />
              <span>Parking</span>
            </div>
          }
          {property?.securityLevel &&
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-white/5 text-white/60 px-2 py-1 rounded-md">
              <Icon name="Shield" size={10} />
              <span>Sécurité</span>
            </div>
          }
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 mt-auto">
          <div className="flex gap-2">
            <Button
              variant={is3DModel ? 'elegant' : 'default'}
              size="sm"
              onClick={handleLaunch3DTour}
              iconName="Box"
              iconPosition="left"
              className={`flex-1 rounded-xl shadow-none border-none ${is3DModel ? 'bg-brand-gold text-black hover:bg-yellow-400 font-bold' : 'bg-white text-black hover:bg-gray-200 font-bold'}`}>
              Visite 3D
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInquiryForm(true)}
              iconName="Eye"
              iconPosition="left"
              className="flex-1 bg-white/5 hover:bg-white/10 text-white border-none rounded-xl">
              Visite
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleQuickInquiry('info')}
            iconName="Info"
            iconPosition="left"
            className="w-full bg-white/5 hover:bg-white/10 text-white border-none rounded-xl">
            Plus d'infos
          </Button>
        </div>
      </div>
      
      {/* Quick Inquiry Modal */}
      {showInquiryForm &&
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#111111] rounded-3xl p-8 w-full max-w-sm shadow-2xl border border-white/10">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-heading font-bold text-xl text-white">Demande rapide</h3>
              <button
              onClick={() => setShowInquiryForm(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white transition-colors border-none">
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <button
              onClick={() => handleQuickInquiry('viewing')}
              className="w-full flex items-center justify-center gap-3 py-4 bg-brand-gold text-black font-bold rounded-2xl hover:bg-yellow-400 transition-colors shadow-lg border-none">
                <Icon name="Eye" size={18} />
                Programmer une visite
              </button>
              <button
              onClick={() => handleQuickInquiry('investment')}
              className="w-full flex items-center justify-center gap-3 py-4 bg-white/5 text-white font-medium rounded-2xl hover:bg-white/10 transition-colors border-none">
                <Icon name="Calculator" size={18} className="text-brand-gold" />
                Analyse d'investissement
              </button>
              <button
              onClick={() => handleQuickInquiry('legal')}
              className="w-full flex items-center justify-center gap-3 py-4 bg-white/5 text-white font-medium rounded-2xl hover:bg-white/10 transition-colors border-none">
                <Icon name="Scale" size={18} className="text-brand-gold" />
                Consultation légale
              </button>
            </div>
          </div>
        </div>
      }
    </div>);
};

export default PropertyCard;